import { DEFAULT_COLORS } from '../constants';
import type { Node } from '../types';

export const getCSSVar = (varName: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(varName).trim();

export const getContrastColor = (hexColor: string): string => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
};

interface NodeColors {
  fillColor: string;
  strokeColor: string;
  textColor: string;
  outlineColor: string;
  outlineWidth: number;
  shadowColor: string;
}

export const getNodeColors = (node: Node): NodeColors => {
  const isProblem = node.type === 'problem';
  const isDark = getCSSVar('--is-dark') === '1';

  const defaultStroke = isProblem
    ? (getCSSVar('--ctp-red') || DEFAULT_COLORS.PROBLEM)
    : (getCSSVar('--ctp-green') || DEFAULT_COLORS.SOLUTION);

  const nodeColor = node.color || defaultStroke;

  let fillColor: string;
  if (node.banned) {
    fillColor = getCSSVar('--ctp-surface1') || DEFAULT_COLORS.BANNED_FILL;
  } else if (node.color) {
    fillColor = isDark
      ? tintDark(node.color, getCSSVar('--ctp-base') || '#1e1e2e', 0.22)
      : lightenColor(node.color, 0.85);
  } else {
    fillColor = isProblem
      ? (getCSSVar('--node-problem-fill') || DEFAULT_COLORS.PROBLEM_FILL)
      : (getCSSVar('--node-solution-fill') || DEFAULT_COLORS.SOLUTION_FILL);
  }

  const strokeColor = node.banned
    ? (getCSSVar('--ctp-overlay0') || DEFAULT_COLORS.BANNED_STROKE)
    : nodeColor;

  const textColor = node.banned
    ? (getCSSVar('--ctp-overlay2') || DEFAULT_COLORS.BANNED_TEXT)
    : getContrastColor(fillColor);

  const outlineColor = node.banned
    ? (getCSSVar('--ctp-overlay0') || DEFAULT_COLORS.BANNED_STROKE)
    : isDark
      ? strokeColor
      : mixHex(fillColor, strokeColor, node.color ? 0.34 : 0.22);

  const outlineWidth = node.banned ? 1.2 : (isDark ? 0 : (isProblem ? 1.4 : 1.2));
  const shadowColor = isDark
    ? 'transparent'
    : (isProblem ? 'rgba(76, 79, 105, 0.08)' : 'rgba(76, 79, 105, 0.06)');

  return { fillColor, strokeColor, textColor, outlineColor, outlineWidth, shadowColor };
};

const parseHex = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)];
};

const tintDark = (hexColor: string, baseHex: string, amount: number): string => {
  const [ar, ag, ab] = parseHex(hexColor);
  const [br, bg, bb] = parseHex(baseHex);
  const r = Math.round(br + (ar - br) * amount);
  const g = Math.round(bg + (ag - bg) * amount);
  const b = Math.round(bb + (ab - bb) * amount);
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
};

const mixHex = (baseHex: string, accentHex: string, amount: number): string => {
  const [br, bg, bb] = parseHex(baseHex);
  const [ar, ag, ab] = parseHex(accentHex);
  const r = Math.round(br + (ar - br) * amount);
  const g = Math.round(bg + (ag - bg) * amount);
  const b = Math.round(bb + (ab - bb) * amount);
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
};

const lightenColor = (hexColor: string, amount: number): string => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);
  return '#' + [newR, newG, newB].map(x => x.toString(16).padStart(2, '0')).join('');
};

export const getDefaultColor = (type: string): string => {
  return type === 'problem' ? DEFAULT_COLORS.PROBLEM : DEFAULT_COLORS.SOLUTION;
};
